import React, { useState } from 'react'
import { ChevronRight, Ban, Clock } from 'lucide-react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { cn, Dialog, DialogContent, DialogHeader, DialogTitle } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import { UserBadge } from '@/components/UserBadge/UserBadge'
import { AssignmentActionsDropdown } from '../../Assignments/AssignmentActionsDropdown'
import { useAssignmentActions } from '../../Assignments/useAssignmentActions'
import { SendAssignment } from '../../Assignments/SendAssignment'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { AssignmentTypeBadge } from '../shared/AssignmentTypeBadge'
import { CompletionRing } from '../shared/CompletionRing'
import { StatusPill } from '../shared/StatusPill'
import { CopyLinkButton } from '../shared/CopyLinkButton'
import { AssignmentRoster } from './AssignmentRoster'
import type { AssignmentGroup } from '../cohortV2Data'
import { toResultsDetails, type ResultsDetails } from './SubmissionResultsDialog'

const S = PartnerService.AssignmentDetailsStatus

interface AssignmentRowProps {
  group: AssignmentGroup
  persons: UserService.Person[]
  getTaskName: (taskId: string) => string
  isTaskLoading: boolean
  onViewResults: (d: ResultsDetails) => void
  onAssignmentChange: () => void
}

const formatTimeLimit = (minutes: number, t: ReturnType<typeof useTar>['t']): string => {
  const whole = minutes % 60 === 0
  const amount = whole ? minutes / 60 : minutes
  const singular = amount === 1
  const unit = whole ? (singular ? t('common.hour') : t('common.hours')) : (singular ? t('common.minute') : t('common.minutes'))
  return `${amount} ${unit}`
}

export const AssignmentRow: React.FC<AssignmentRowProps> = ({ group, persons, getTaskName, isTaskLoading, onViewResults, onAssignmentChange }) => {
  const { t } = useTar()
  const { tP } = usePartnerTar() // org-type wording for the person-count noun (candidates/students)
  const { assignment, kind, details, completion } = group
  const expandable = kind !== 'single'
  const [open, setOpen] = useState(false)
  const {
    handleEdit, handleDelete, handleSetStatus, isActive,
    isEditDialogOpen, setIsEditDialogOpen, isDeleteDialogOpen, setIsDeleteDialogOpen,
  } = useAssignmentActions({ assignment, onAssignmentChange })

  const isPublic = kind === 'public'
  const dueDatePast = assignment.dueDate ? new Date(assignment.dueDate) < new Date() : false

  const only = kind === 'single' ? details[0] : undefined
  const onlyPerson = only ? persons.find((p) => p.id === only.personId) : undefined
  const onlyName = only ? (onlyPerson ? `${onlyPerson.firstName || ''} ${onlyPerson.lastName || ''}`.trim() || only.personId : only.personId) : ''
  const onlyGraded = !!only?.feedback && only?.grade !== undefined

  // The whole row is interactive: multi/public toggle the roster; a single graded assignment
  // opens its results directly (no separate "View results" link).
  const singleClickable = kind === 'single' && onlyGraded && !!only
  const interactive = expandable || singleClickable
  const activateRow = () => {
    if (expandable) setOpen((v) => !v)
    else if (only) onViewResults(toResultsDetails(only, onlyName, onlyPerson))
  }
  const interactiveProps = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        ...(expandable ? { 'aria-expanded': open } : {}),
        onClick: activateRow,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateRow() }
        },
      }
    : {}

  const actions = (
    <div onClick={(e) => e.stopPropagation()} className="flex items-center">
      <AssignmentActionsDropdown
        isActive={isActive}
        onEdit={handleEdit}
        onDeleteClick={() => setIsDeleteDialogOpen(true)}
        onSetStatus={handleSetStatus}
        linkToken={isPublic ? assignment.linkToken : undefined}
      />
    </div>
  )

  return (
    <div className={cn(
      'mb-2 overflow-hidden rounded-xl border',
      isPublic ? 'border-primary/20 bg-primary/[0.07]' : 'border-border bg-card/50',
    )}>
      <div
        className={cn('flex h-[62px] items-center gap-3 px-3.5', interactive && 'cursor-pointer hover:bg-primary/10')}
        {...interactiveProps}
      >
        <AssignmentTypeBadge kind={kind} />
        {expandable
          ? <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open ? 'rotate-90' : 'rtl:rotate-180')} />
          : <span className="w-3.5 shrink-0" />}

        <span className="min-w-0 flex-1 truncate font-semibold text-foreground lg:w-[230px] lg:flex-none">
          {isTaskLoading ? t('common.loading') : getTaskName(assignment.taskId)}
        </span>

        {/* Lifecycle state badges — always visible (even collapsed); replaces the old opacity dim. */}
        {(!isActive || dueDatePast) && (
          <div className="flex shrink-0 items-center gap-1.5">
            {!isActive && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <Ban className="h-3 w-3" aria-hidden="true" />
                {t('common.inactive')}
              </span>
            )}
            {dueDatePast && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-500">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {t('partner.overdue')}
              </span>
            )}
          </div>
        )}

        <div className="flex min-w-0 flex-none items-center gap-3 text-xs text-muted-foreground lg:flex-1">
          {assignment.timeLimit != null && assignment.timeLimit > 0 && <span className="hidden lg:inline">⏱ {formatTimeLimit(assignment.timeLimit, t)}</span>}
          {isPublic
            ? <span className="hidden lg:inline">{t('partner.anyoneWithLink')}</span>
            : assignment.createdAt && <span className="hidden text-foreground lg:inline">{t('common.sent')} <RelativeDateTime isoDateString={assignment.createdAt} className="text-xs" /></span>}
          {kind === 'single' && only && (
            <UserBadge
              userId={onlyPerson?.userId ?? ''}
              firstName={onlyPerson?.firstName}
              lastName={onlyPerson?.lastName}
              fullName={onlyName}
              email={onlyPerson?.email}
              className="min-w-0"
            />
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {isPublic && assignment.linkToken && <CopyLinkButton linkToken={assignment.linkToken} className="hidden lg:inline-flex" />}
          {expandable && (
            <>
              <span className={cn(
                'whitespace-nowrap rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs text-foreground',
                // On mobile a state badge (inactive/overdue) takes priority — hide the count there.
                (!isActive || dueDatePast) && 'hidden lg:inline'
              )}>
                {isPublic ? t('partner.joinedCount', { count: completion.total }) : tP('assignedPeopleCount', { count: completion.total })}
              </span>
              {/* Public rosters can grow indefinitely, so a "submitted / total" ring is meaningless there. */}
              {!isPublic && <CompletionRing submitted={completion.submitted} total={completion.total} />}
            </>
          )}
          {kind === 'single' && only && (
            onlyGraded ? (
              <GradeBadge grade={only.grade as number} small />
            ) : (
              <StatusPill status={only.status} processing={only.status === S.Submitted && !onlyGraded} />
            )
          )}
          {actions}
        </div>
      </div>

      {expandable && open && (
        <AssignmentRoster details={details} persons={persons} onViewResults={onViewResults} />
      )}

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent width="xs">
          <DialogHeader><DialogTitle>{t('common.editAssignment')}</DialogTitle></DialogHeader>
          <SendAssignment
            cohortId={assignment.cohortId}
            personIds={assignment.personIds}
            assignmentId={assignment.id}
            onClose={() => setIsEditDialogOpen(false)}
            onSend={() => { setIsEditDialogOpen(false); onAssignmentChange() }}
          />
        </DialogContent>
      </Dialog>

      <DeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        title={t('common.deleteAssignment')}
        description={t('common.assignmentDeletedDescription')}
      />
    </div>
  )
}
